import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import * as adminApi from '../../../api/adminApi';

/**
 * Read-only ingestion freshness view (Phase-1 #9: "at backend which data got
 * synced / not synced is missing"). Lists each source with artifact counts,
 * last-seen time, a freshness flag, and the latest ingestion run. No mutations.
 */
const INVENTORY_PAGE = 50;

/** Buckets in the order an administrator should read them: what we can publish,
 *  what was deliberately set aside, what nobody has looked at, and the fault. */
const BUCKET_ORDER: adminApi.CorpusBucket[] = ['published', 'screened', 'unassessed', 'missing_text'];
const BUCKET_TITLE: Record<adminApi.CorpusBucket, string> = {
  published: 'Full text',
  screened: 'Screened out',
  unassessed: 'Not yet assessed',
  missing_text: 'Relevant, no text',
};
const BUCKET_TONE: Record<adminApi.CorpusBucket, string> = {
  published: 'var(--ds-success)',
  screened: 'var(--ds-text-secondary)',
  unassessed: 'var(--ds-warning)',
  missing_text: 'var(--ds-danger)',
};

const DataSyncStatusPage: React.FC = () => {
  const {
    sidebarCollapsed,
    setSidebarCollapsed,
    activeSection,
    setSelectedRegulation,
    handleNavClick,
    handleLogout,
  } = useAdminNavigation();

  const [rows, setRows] = useState<adminApi.DataSyncSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [runMsg, setRunMsg] = useState<{ source: string; text: string; ok: boolean } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [runs, setRuns] = useState<adminApi.IngestionRunRow[]>([]);
  const [alerts, setAlerts] = useState<adminApi.SyncAlertRow[]>([]);
  const [checking, setChecking] = useState(false);
  const [inventory, setInventory] = useState<adminApi.CorpusInventory | null>(null);
  const [invBucket, setInvBucket] = useState<adminApi.CorpusBucket | null>(null);
  const [invSource, setInvSource] = useState<string>('');
  const [invOffset, setInvOffset] = useState(0);
  const [invLoading, setInvLoading] = useState(false);
  const [invError, setInvError] = useState<string | null>(null);

  const token = window.localStorage.getItem('policyora_token');

  const fetchStatus = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    adminApi
      .getDataSyncStatus(token)
      .then((d) => { setRows(d.sources); setGeneratedAt(d.generated_at); })
      .then(() => adminApi.getSyncAlerts(token).then((a) => setAlerts(a.alerts)).catch(() => {}))
      .catch((err) => setError(err.message || 'Failed to load sync status'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleRun = useCallback((source: string) => {
    if (!token) return;
    setTriggering(source);
    setRunMsg(null);
    adminApi
      .triggerIngestionRun(token, source)
      .then(() => {
        setRunMsg({ source, text: 'Queued — the worker will pick it up shortly. Refresh in a minute to see the run.', ok: true });
        // Give the worker a moment to register the run, then refresh the table.
        setTimeout(fetchStatus, 5000);
      })
      .catch((err) => setRunMsg({ source, text: err.message || 'Failed to queue run', ok: false }))
      .finally(() => setTriggering(null));
  }, [token, fetchStatus]);

  // The inventory is a second, heavier read, so it is fetched on its own and the
  // page stays usable if it fails: the tile falls back to the plain artifact count.
  const loadInventory = useCallback((bucket: adminApi.CorpusBucket | null, source: string, offset: number) => {
    if (!token) return;
    setInvLoading(true);
    setInvError(null);
    adminApi
      .getCorpusInventory(token, {
        bucket: bucket ?? undefined,
        sourceSystem: source || undefined,
        limit: INVENTORY_PAGE,
        offset,
      })
      .then(setInventory)
      .catch((err) => setInvError(err.message || 'Failed to load corpus inventory'))
      .finally(() => setInvLoading(false));
  }, [token]);

  useEffect(() => { loadInventory(invBucket, invSource, invOffset); }, [loadInventory, invBucket, invSource, invOffset]);

  // Where each source's documents actually come from. Derived from the records,
  // so it cannot go stale the way a hand-kept list of sites would, and a source
  // whose records carry no link simply shows nothing.
  const publishers = useMemo(() => {
    const out: Record<string, string> = {};
    (inventory?.by_source || []).forEach((s) => { if (s.publisher) out[s.source_system] = s.publisher; });
    return out;
  }, [inventory]);

  const openBucket = useCallback((bucket: adminApi.CorpusBucket) => {
    setInvOffset(0);
    setInvBucket((current) => (current === bucket ? null : bucket));
  }, []);

  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
  const totalArtifacts = rows.reduce((s, r) => s + (r.artifact_count || 0), 0);
  const staleCount = rows.filter((r) => r.state === 'stale').length;
  const errorCount = rows.filter((r) => r.state === 'error').length;

  const openRuns = useCallback((source: string) => {
    if (!token) return;
    if (expanded === source) { setExpanded(null); return; }
    setExpanded(source);
    adminApi.getSourceRuns(token, source).then((d) => setRuns(d.runs)).catch(() => setRuns([]));
  }, [token, expanded]);

  const toggleSource = useCallback((source: string, enabled: boolean) => {
    if (!token) return;
    adminApi.setSourceEnabled(token, source, enabled).then(fetchStatus).catch((err) => setError(err.message || 'Failed to update source'));
  }, [token, fetchStatus]);

  const checkNow = useCallback(() => {
    if (!token) return;
    setChecking(true);
    adminApi.runSyncCheck(token).then(fetchStatus).catch((err) => setError(err.message || 'Check failed')).finally(() => setChecking(false));
  }, [token, fetchStatus]);

  const STATE: Record<string, { label: string; bg: string; fg: string }> = {
    active: { label: 'Active', bg: 'rgba(18,183,106,0.12)', fg: 'var(--ds-success)' },
    stale: { label: 'Stale', bg: 'rgba(247,144,9,0.14)', fg: 'var(--ds-warning)' },
    error: { label: 'Error', bg: 'rgba(240,68,56,0.12)', fg: 'var(--ds-danger)' },
    never_run: { label: 'Never run', bg: 'rgba(102,112,133,0.12)', fg: 'var(--ds-text-secondary)' },
    disabled: { label: 'Disabled', bg: 'rgba(102,112,133,0.12)', fg: 'var(--ds-text-secondary)' },
    no_pipeline: { label: 'Seeded', bg: 'transparent', fg: 'var(--ds-text-faint)' },
  };

  const th: React.CSSProperties = { textAlign: 'left', padding: '0.6rem 0.8rem', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ds-text-muted)', borderBottom: '1px solid var(--ds-border)', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '0.6rem 0.8rem', fontSize: '0.85rem', color: 'var(--ds-text)', borderBottom: '1px solid var(--ds-surface-2)', whiteSpace: 'nowrap' };

  return (
    <DashboardLayout
      collapsed={sidebarCollapsed}
      activeSection={activeSection}
      welcomeLabel="Data Sync Status"
      selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((prev) => !prev)}
      onNavClick={handleNavClick}
      onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout}
      onRegulationModalClose={() => setSelectedRegulation(null)}
    >
      <section style={{ padding: '1.25rem 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: 'var(--ds-text)' }}>Data Sync Status</h2>
            <p style={{ margin: '4px 0 0', color: 'var(--ds-text-muted)', fontSize: '0.88rem' }}>
              Source-by-source sync health: state, last successful sync, errors, and alerts. Sources can be switched off here at runtime.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchStatus}
            style={{ padding: '0.45rem 0.9rem', borderRadius: 8, border: '1px solid var(--ds-border-strong)', background: 'transparent', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ds-text-secondary)' }}
          >
            ↺ Refresh
          </button>
        </div>

        {!loading && !error && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ background: 'var(--ds-surface-2)', border: '1px solid var(--ds-border)', borderRadius: 10, padding: '0.7rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--ds-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sources</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--ds-text)' }}>{rows.length}</div>
            </div>
            <div style={{ background: 'var(--ds-surface-2)', border: '1px solid var(--ds-border)', borderRadius: 10, padding: '0.7rem 1rem' }} data-testid="artifacts-tile">
              {/* Leads with the count a customer can actually see, which is what
                  Statistics calls "Total policies tracked" — the two admin screens
                  disagreed, one showing every artifact held and the other only the
                  ones with usable text. The raw total stays below, because this is
                  the sync screen and what a source has fetched still matters here. */}
              <div style={{ fontSize: '0.72rem', color: 'var(--ds-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {inventory ? 'Policies tracked' : 'Total artifacts'}
              </div>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--ds-text)' }}>
                {(inventory ? inventory.totals.published : totalArtifacts).toLocaleString()}
              </div>
              {inventory && (
                <div style={{ marginTop: 4, fontSize: '0.74rem', color: 'var(--ds-text-muted)' }} data-testid="artifacts-split">
                  of {inventory.totals.total.toLocaleString()} artifacts held ·{' '}
                  {(inventory.totals.total - inventory.totals.published).toLocaleString()} without full text
                </div>
              )}
            </div>
            <div style={{ background: staleCount ? 'rgba(240,68,56,0.06)' : 'var(--ds-surface-2)', border: `1px solid ${staleCount ? 'rgba(240,68,56,0.3)' : 'var(--ds-border)'}`, borderRadius: 10, padding: '0.7rem 1rem' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--ds-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Stale (&gt;30d)</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: staleCount ? 'var(--ds-danger)' : 'var(--ds-text)' }}>{staleCount}</div>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--ds-text-muted)' }}>Loading sync status…</div>
        ) : error ? (
          <div style={{ padding: '1rem', borderRadius: 8, background: 'rgba(240,68,56,0.08)', color: 'var(--ds-danger-text)' }}>
            {error} <button type="button" onClick={fetchStatus} style={{ marginLeft: 8, cursor: 'pointer' }}>Retry</button>
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--ds-text-muted)' }}>No ingestion data found.</div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--ds-border)', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={th}>Source</th>
                  <th style={th}>State</th>
                  <th style={th}>Artifacts</th>
                  <th style={th}>Last successful sync</th>
                  <th style={th}>Last run</th>
                  <th style={th}>Error</th>
                  <th style={th}>Enabled</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <React.Fragment key={r.source_system}>
                  <tr>
                    <td style={{ ...td, fontWeight: 600 }}>
                      <button type="button" onClick={() => openRuns(r.source_system)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 600, color: 'var(--ds-text)', padding: 0 }}>
                        {expanded === r.source_system ? '▾' : '▸'} {r.source_system}
                      </button>
                      {r.open_alerts.length > 0 && <div style={{ fontSize: '0.7rem', color: 'var(--ds-danger-text)' }}>alert: {r.open_alerts.join(', ')}</div>}
                      {publishers[r.source_system] && (
                        <a
                          href={`https://${publishers[r.source_system]}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`publisher-${r.source_system}`}
                          style={{ fontSize: '0.7rem', color: 'var(--ds-text-muted)' }}
                          title="The site this source's documents come from"
                        >
                          {publishers[r.source_system]} ↗
                        </a>
                      )}
                    </td>
                    <td style={td}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, borderRadius: 999, padding: '0.1rem 0.5rem', background: STATE[r.state]?.bg, color: STATE[r.state]?.fg }}>{STATE[r.state]?.label || r.state}</span>
                      {r.failures_24h > 0 && <div style={{ fontSize: '0.7rem', color: 'var(--ds-danger-text)' }}>{r.failures_24h} failure{r.failures_24h === 1 ? '' : 's'} in 24h</div>}
                    </td>
                    <td style={td}>{r.artifact_count.toLocaleString()}</td>
                    <td style={td}>
                      {r.has_pipeline ? (
                        <>
                          {fmt(r.last_checked_at || r.last_successful_sync_at)}
                          <div style={{ fontSize: '0.7rem', color: 'var(--ds-text-faint)' }}>
                            expected every {r.expected_cadence_hours}h
                            {r.last_checked_at && r.last_successful_sync_at && r.last_checked_at !== r.last_successful_sync_at
                              && ` · last change ${fmt(r.last_successful_sync_at)}`}
                          </div>
                        </>
                      ) : (
                        <>
                          {fmt(r.first_seen)}
                          <div style={{ fontSize: '0.7rem', color: 'var(--ds-text-faint)' }}>loaded once · does not refresh</div>
                        </>
                      )}
                    </td>
                    <td style={td}>{fmt(r.last_run_at)}<div style={{ fontSize: '0.7rem', color: 'var(--ds-text-faint)' }}>{r.last_run_status || '—'}{r.last_run_succeeded != null ? ` · ${r.last_run_succeeded} ok / ${r.last_run_failed ?? 0} failed` : ''}</div></td>
                    <td style={{ ...td, whiteSpace: 'normal', maxWidth: 280, color: 'var(--ds-danger-text)', fontSize: '0.78rem' }}>{r.last_error || <span style={{ color: 'var(--ds-text-faint)' }}>—</span>}</td>
                    <td style={td}>
                      {r.has_pipeline ? (
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', cursor: 'pointer' }} title={r.env_enabled === false ? 'Switched off in the environment (ENABLE_* flag)' : ''}>
                          <input type="checkbox" checked={!r.admin_disabled} disabled={r.env_enabled === false} onChange={(e) => toggleSource(r.source_system, e.target.checked)} />
                          {r.env_enabled === false ? 'off (env)' : r.admin_disabled ? 'off' : 'on'}
                        </label>
                      ) : <span style={{ color: 'var(--ds-text-faint)', fontSize: '0.78rem' }}>—</span>}
                    </td>
                    <td style={td}>
                      {r.can_run ? (
                        <>
                          <button type="button" onClick={() => handleRun(r.source_system)} disabled={triggering === r.source_system || !r.enabled}
                            title={!r.enabled ? 'Enable the source first' : ''}
                            style={{ padding: '0.3rem 0.7rem', borderRadius: 8, border: '1px solid var(--ds-border-strong)', background: triggering === r.source_system ? 'var(--ds-surface-2)' : 'var(--ds-surface)', cursor: triggering === r.source_system || !r.enabled ? 'default' : 'pointer', fontSize: '0.78rem', fontWeight: 600, color: 'var(--ds-text-secondary)', opacity: r.enabled ? 1 : 0.5 }}>
                            {triggering === r.source_system ? 'Queuing…' : '▶ Run now'}
                          </button>
                          {runMsg && runMsg.source === r.source_system && (
                            <div style={{ marginTop: 4, fontSize: '0.72rem', color: runMsg.ok ? 'var(--ds-success)' : 'var(--ds-danger)', whiteSpace: 'normal', maxWidth: 240 }}>{runMsg.text}</div>
                          )}
                        </>
                      ) : <span style={{ color: 'var(--ds-text-faint)', fontSize: '0.78rem' }}>—</span>}
                    </td>
                  </tr>
                  {expanded === r.source_system && (
                    <tr>
                      <td colSpan={8} style={{ padding: '0.5rem 0.8rem 0.9rem', background: 'var(--ds-surface-2)', borderBottom: '1px solid var(--ds-surface-2)' }}>
                        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ds-text-muted)', marginBottom: 6 }}>Recent runs</div>
                        {runs.length === 0 ? <div style={{ fontSize: '0.8rem', color: 'var(--ds-text-faint)' }}>{rows.find((x) => x.source_system === expanded)?.has_pipeline ? 'No runs recorded yet.' : 'Loaded by hand — this source has no pipeline, so there are no runs to show.'}</div> : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                            <thead><tr><th style={th}>Started</th><th style={th}>Status</th><th style={th}>Duration</th><th style={th}>Attempted / OK / Failed</th><th style={th}>Error</th></tr></thead>
                            <tbody>{runs.map((x) => (
                              <tr key={x.run_id}>
                                <td style={td}>{fmt(x.started_at)}</td>
                                <td style={{ ...td, color: x.status === 'failed' ? 'var(--ds-danger)' : x.status === 'running' ? 'var(--ds-warning)' : 'var(--ds-success)' }}>{x.status}</td>
                                <td style={td}>{x.duration_seconds != null ? `${x.duration_seconds}s` : '—'}</td>
                                <td style={td}>{x.attempted ?? 0} / {x.succeeded ?? 0} / {x.failed ?? 0}</td>
                                <td style={{ ...td, whiteSpace: 'normal', color: 'var(--ds-danger-text)' }}>{x.error_message || ''}</td>
                              </tr>
                            ))}</tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 18, border: '1px solid var(--ds-border)', borderRadius: 10, padding: '0.9rem 1rem' }} data-testid="corpus-inventory">
          <div style={{ fontWeight: 700, color: 'var(--ds-text)' }}>Corpus inventory</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--ds-text-muted)', marginTop: 2 }}>
            Why the artifact count above and the regulation count on the dashboards differ. An instrument is
            published once we hold {inventory ? inventory.floor_chars.toLocaleString() : '1,000'} characters or more of
            normalised text; everything else is listed here, with its source, so any number on this page can be checked.
          </div>

          {invError && <div style={{ marginTop: 10, fontSize: '0.82rem', color: 'var(--ds-danger)' }}>{invError}</div>}

          {inventory && (
            <>
              <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                {BUCKET_ORDER.map((b) => {
                  const selected = invBucket === b;
                  return (
                    <button
                      key={b}
                      type="button"
                      onClick={() => openBucket(b)}
                      aria-pressed={selected}
                      title={inventory.labels[b]}
                      style={{
                        textAlign: 'left', cursor: 'pointer', borderRadius: 10, padding: '0.55rem 0.85rem',
                        background: selected ? 'var(--ds-surface-2)' : 'transparent',
                        border: `1px solid ${selected ? 'var(--ds-border-strong)' : 'var(--ds-border)'}`,
                      }}
                    >
                      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ds-text-muted)' }}>{BUCKET_TITLE[b]}</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: BUCKET_TONE[b] }}>{inventory.totals[b].toLocaleString()}</div>
                    </button>
                  );
                })}
              </div>

              {invBucket && (
                <div style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--ds-text-secondary)' }}>
                  {inventory.labels[invBucket]}
                  {invBucket !== 'published' && (
                    <> · {inventory.totals.linked.toLocaleString()} of the {(inventory.totals.total - inventory.totals.published).toLocaleString()} unpublished records carry an official link</>
                  )}
                </div>
              )}

              <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <label htmlFor="inv-source" style={{ fontSize: '0.78rem', color: 'var(--ds-text-muted)' }}>Source</label>
                <select
                  id="inv-source"
                  value={invSource}
                  onChange={(e) => { setInvOffset(0); setInvSource(e.target.value); }}
                  style={{ padding: '0.3rem 0.5rem', borderRadius: 8, border: '1px solid var(--ds-border)', background: 'var(--ds-surface)', color: 'var(--ds-text)', fontSize: '0.82rem' }}
                >
                  <option value="">All sources</option>
                  {inventory.by_source.map((sRow) => (
                    <option key={sRow.source_system} value={sRow.source_system}>
                      {sRow.source_system} ({sRow.total.toLocaleString()})
                    </option>
                  ))}
                </select>
                {invLoading && <span style={{ fontSize: '0.78rem', color: 'var(--ds-text-faint)' }}>Loading…</span>}
              </div>

              <div style={{ overflowX: 'auto', marginTop: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Title</th>
                      <th style={th}>Jurisdiction</th>
                      <th style={th}>Source</th>
                      <th style={th}>Relevance</th>
                      <th style={th}>Text</th>
                      <th style={th}>Official source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventory.listing.items.map((rec) => (
                      <tr key={rec.id}>
                        <td style={{ ...td, whiteSpace: 'normal', maxWidth: 380 }}>{rec.title}</td>
                        <td style={td}>{rec.jurisdiction || '—'}</td>
                        <td style={td}>{rec.source_system}</td>
                        <td style={td}>{rec.relevance_status || 'not assessed'}</td>
                        <td style={td}>{rec.text_chars ? `${rec.text_chars.toLocaleString()} chars` : 'none'}</td>
                        <td style={td}>
                          {rec.source_url
                            ? <a href={rec.source_url} target="_blank" rel="noopener noreferrer">Open ↗</a>
                            : <span style={{ color: 'var(--ds-text-faint)' }}>no link recorded</span>}
                        </td>
                      </tr>
                    ))}
                    {inventory.listing.items.length === 0 && !invLoading && (
                      <tr><td style={td} colSpan={6}>Nothing in this selection.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', fontSize: '0.8rem', color: 'var(--ds-text-muted)' }}>
                <span>
                  {inventory.listing.total === 0
                    ? 'No records'
                    : `${(inventory.listing.offset + 1).toLocaleString()}–${Math.min(inventory.listing.offset + inventory.listing.items.length, inventory.listing.total).toLocaleString()} of ${inventory.listing.total.toLocaleString()}`}
                </span>
                <button type="button" disabled={invOffset === 0 || invLoading} onClick={() => setInvOffset(Math.max(0, invOffset - INVENTORY_PAGE))} style={{ padding: '0.25rem 0.6rem', borderRadius: 8, border: '1px solid var(--ds-border)', background: 'transparent', cursor: 'pointer' }}>Previous</button>
                <button type="button" disabled={invOffset + INVENTORY_PAGE >= inventory.listing.total || invLoading} onClick={() => setInvOffset(invOffset + INVENTORY_PAGE)} style={{ padding: '0.25rem 0.6rem', borderRadius: 8, border: '1px solid var(--ds-border)', background: 'transparent', cursor: 'pointer' }}>Next</button>
              </div>
            </>
          )}
        </div>

        <div style={{ marginTop: 18, border: '1px solid var(--ds-border)', borderRadius: 10, padding: '0.9rem 1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 700, color: 'var(--ds-text)' }}>Open alerts ({alerts.length})</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--ds-text-muted)' }}>The monitor runs hourly; each alert is emailed once to the ops address and resolves itself when the source recovers.</div>
            </div>
            <button type="button" onClick={checkNow} disabled={checking} style={{ padding: '0.35rem 0.8rem', borderRadius: 8, border: '1px solid var(--ds-border-strong)', background: 'var(--ds-surface)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>{checking ? 'Checking…' : 'Check now'}</button>
          </div>
          {alerts.length > 0 && (
            <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: '0.82rem', color: 'var(--ds-text-secondary)' }}>
              {alerts.map((a) => <li key={a.id}><strong>{a.source_system}</strong> · {a.kind} · {a.message} <span style={{ color: 'var(--ds-text-faint)' }}>({fmt(a.created_at)}{a.notified ? ', emailed' : ', not emailed'})</span></li>)}
            </ul>
          )}
          {errorCount + staleCount > 0 && alerts.length === 0 && <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--ds-warning-text)' }}>{errorCount} in error, {staleCount} stale — press "Check now" to raise alerts.</div>}
        </div>

        {generatedAt && !loading && (
          <p style={{ marginTop: 12, color: 'var(--ds-text-faint)', fontSize: '0.78rem' }}>Generated {fmt(generatedAt)}</p>
        )}
      </section>
    </DashboardLayout>
  );
};

export default DataSyncStatusPage;
